import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { TaxRate } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import { countryName } from '@/features/markets';

import { fractionToPercent } from '../schemas/tax-rate-rules';

export type TaxRateStatus = 'active' | 'inactive';

export const taxRateStatus = (rate: TaxRate): TaxRateStatus =>
  rate.isActive ? 'active' : 'inactive';

/** Column model for the tax rates table. Sortable by name, place, rate and priority. */
export function useTaxRateColumns() {
  const { t } = useTranslation('tax');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<TaxRate>>>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('columns.name'),
        meta: { sortField: 'name', lockVisibility: true },
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        id: 'where',
        accessorFn: (row) =>
          row.region
            ? `${countryName(row.countryCode)} · ${row.region}`
            : countryName(row.countryCode),
        header: t('columns.where'),
        meta: { sortField: 'where', filterKey: 'country' },
        cell: ({ row }) => (
          <>
            {countryName(row.original.countryCode)}
            {row.original.region ? (
              <span className="text-muted-foreground"> · {row.original.region}</span>
            ) : null}
          </>
        ),
      },
      {
        id: 'postalPattern',
        accessorFn: (row) => row.postalPattern ?? '',
        header: t('columns.postalPattern'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.postalPattern ? (
            <code className="text-[13px]">{row.original.postalPattern}</code>
          ) : (
            <span className="text-muted-foreground">{t('anyPostal')}</span>
          ),
      },
      {
        id: 'rate',
        accessorKey: 'rate',
        header: t('columns.rate'),
        meta: { sortField: 'rate', align: 'end', searchable: false },
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('percent', {
              value: format.number(fractionToPercent(row.original.rate), {
                maximumFractionDigits: 4,
              }),
            })}
          </span>
        ),
      },
      {
        id: 'appliesToShipping',
        accessorKey: 'appliesToShipping',
        header: t('columns.shipping'),
        enableSorting: false,
        meta: { searchable: false },
        cell: ({ row }) => (row.original.appliesToShipping ? t('yes') : t('no')),
      },
      {
        id: 'priority',
        accessorKey: 'priority',
        header: t('columns.priority'),
        meta: { sortField: 'priority', align: 'end', searchable: false },
        cell: ({ row }) => <span className="tabular-nums">{row.original.priority}</span>,
      },
      {
        id: 'status',
        accessorFn: taxRateStatus,
        header: t('columns.active'),
        enableSorting: false,
        meta: { filterKey: 'status', searchable: false },
        cell: ({ row }) => (
          <Badge variant="outline">
            {row.original.isActive ? t('status.active') : t('status.inactive')}
          </Badge>
        ),
      },
    ],
    [format, t],
  );
}
